/**
 * Price Utilities — tick-size inference and tick-aligned rounding.
 *
 * Tick size is inferred from actual candle price data, so no external
 * symbol metadata is required.  Rules (Indian market conventions):
 *   • All sampled prices are whole numbers          → tick = 1.0
 *   • All fractional parts are multiples of 0.05   → tick = 0.05
 *   • Otherwise (finer precision present)           → tick = 0.01
 *
 * Default is 0.05 when candles are unavailable (safe for NSE/BSE/F&O).
 */
import { CandleData } from "../../types/chart.types";

/**
 * Infers the tick size of an instrument by analysing recent candle prices.
 * Samples up to 50 candles (close, high, low) from the end of the array.
 */
export function inferTickSize(candles: CandleData[]): number {
  if (candles.length === 0) return 0.05;

  const n = Math.min(50, candles.length);
  let allInteger = true;
  let allFiveCents = true;

  for (let i = candles.length - n; i < candles.length; i++) {
    const c = candles[i];
    for (const p of [c.close, c.high, c.low]) {
      // Scale to hundredths and look at fractional part
      const hundredths = Math.round(p * 100) % 100; // 0 … 99
      if (hundredths !== 0) allInteger = false;
      if (hundredths % 5 !== 0) allFiveCents = false;
      if (!allInteger && !allFiveCents) return 0.01; // early exit
    }
  }

  if (allInteger) return 1.0;
  if (allFiveCents) return 0.05;
  return 0.01;
}

/**
 * Rounds `value` to the nearest multiple of `tickSize`.
 * e.g. roundToTick(22356.43, 0.05) → 22356.45
 */
export function roundToTick(value: number, tickSize: number): number {
  if (tickSize <= 0) return value;
  return Math.round(value / tickSize) * tickSize;
}
